import React, { useEffect, useState } from 'react'
import { Icon } from '@iconify/react';
import { Button } from '@mui/material';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { useRestaurant } from '../contexts/RestaurantContext';
import { settingNav } from '../components/NavigationPath';
import Loader from '../components/Loader';
import Layout2 from '../components/Layout2';
import AddRestaurant from '../components/AddRestaurant';
import { Badge } from 'primereact/badge';
import { useAuth } from '../contexts/AuthContext';

function RestaurantList() {
    const { getUserRestaurants, deleteRestaurant } = useRestaurant()
    const { auth, restaurantId } = useAuth()

    const [restaurantList, setRestaurantList] = useState()
    const [visible, setVisible] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editRestaurantId, setEditRestaurantId] = useState(null);
    const [sortField, setSortField] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState(-1);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const userPermissions = (auth.user?.is_super_admin || auth.user?.restaurantIds?.length > 1) ? auth.user?.permissions : auth?.user?.permissions?.map(permission => permission.module) || [];
    const fetchRestaurants = async () => {
        try {
            setIsLoading(true);
            const restaurants = await getUserRestaurants(currentPage, rowsPerPage, sortField, sortOrder, null);
            if (restaurants.length !== 0) {
                setRestaurantList(restaurants.data)
                setTotalRecords(restaurants.totalCount)
            }
            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching roles', error);
        }
    };

    useEffect(() => {
        fetchRestaurants();
    }, [currentPage, rowsPerPage, sortField, sortOrder]);

    const handleSorting = async (e) => {
        const field = e.sortField;
        const order = e.sortOrder;

        setSortField(field);
        setSortOrder(order);
        fetchRestaurants()
    };

    const handleClose = () => {
        setVisible(false);
        setEditMode(false);
        setEditRestaurantId(null);
    };

    const onPageChange = (event) => {
        const newCurrentPage = Math.floor(event.first / event.rows) + 1;
        setCurrentPage(newCurrentPage);
        const newRowsPerPage = event.rows;
        setRowsPerPage(newRowsPerPage);
    };

    const handleDelete = async (id) => {
        try {
            const confirmed = await confirmDelete();

            if (confirmed) {
                await deleteRestaurant(id);
                handleClose();
                fetchRestaurants();
            }
        } catch (error) {
            console.error('Error during delete operation', error);
        }
    }

    const confirmDelete = async () => {
        return new Promise((resolve) => {
            confirmDialog({
                message: 'Are you sure you want to delete this restaurant?',
                header: 'Delete Confirmation',
                icon: 'pi pi-info-circle',
                position: 'top',
                accept: () => resolve(true),
                reject: () => resolve(false),
            });
        });
    };

    const filteredItems = settingNav.items.filter((item) => {
        if (item.to === '/admin/settings/company' && !userPermissions.includes("settings")) {
            return false;
        }
        return true;
    });

    return (
        <Layout2 items={filteredItems} navTitle={settingNav.header}>
            <ConfirmDialog />
            <div className="d-flex flex-column w-100">
                <div className='d-flex flex-row px-4 py-2 w-100 border-bottom'>
                    <div className='w-100'>
                        <h4>Restaurants</h4>
                    </div>
                    <div className='d-flex justify-content-end w-100'>
                        <div className='align-self-end header_btn_container'>
                            <Button variant="outlined" color='success' type="submit" onClick={() => setVisible(true)}>Add new restaurant</Button>
                        </div>
                    </div>
                </div>
                <div className='px-4 py-2'>
                    {
                        (visible || (editMode && editRestaurantId)) && (
                            <AddRestaurant
                                visible={visible}
                                editRestaurantId={editRestaurantId}
                                editMode={editMode}
                                fetchRestaurants={fetchRestaurants}
                                handleClose={handleClose}
                            />
                        )
                    }
                    {isLoading ? (
                        <Loader />
                    ) : (
                        <DataTable
                            className="customerTable thead-dark"
                            totalRecords={totalRecords}
                            lazy
                            paginator
                            sortField={sortField}
                            sortOrder={sortOrder}
                            onSort={(e) => {
                                if (!e.sortField && e.sortOrder === 0) {
                                    handleSorting({ sortField: 'createdAt', sortOrder: -1 });
                                } else {
                                    handleSorting(e);
                                }
                            }}
                            removableSort
                            rows={rowsPerPage}
                            value={restaurantList}
                            first={(currentPage - 1) * rowsPerPage}
                            onPage={onPageChange}
                            dataKey="id"
                            emptyMessage="No restaurant found."
                        >
                            <Column
                                field="name"
                                header="Name"
                                className='restaurant_name'
                                sortable
                                filterField="name"
                                body={(rowData) => (
                                    <div>
                                        {rowData.name}
                                    </div>
                                )}
                            />
                            <Column
                                field="address"
                                header="Address"
                                filterField="type"
                                className='address'
                                body={(rowData) => (
                                    <div title={rowData.address}>
                                        {rowData.address}
                                    </div>
                                )}
                            />
                            <Column
                                field="isActive"
                                header="Is Active"
                                filterField="type"
                                className='isActive'
                                body={(rowData) => (
                                    <div>
                                        {rowData.isActive === 0 ? <Badge value='No' severity="danger"></Badge> : <Badge value='Yes' severity="success"></Badge>}
                                    </div>
                                )}
                            />
                            <Column
                                header="Action"
                                className='action_td'
                                align="left"
                                body={(rowData) => (
                                    <>
                                        <i
                                            className="pi pi-pencil pointerCursor"
                                            title='Edit'
                                            onClick={() => {
                                                setEditMode(true);
                                                setEditRestaurantId(rowData.id)
                                            }}
                                        />
                                        <i
                                            className="pi pi-trash ms-2 pointerCursor"
                                            title='Delete'
                                            aria-label="Cancel"
                                            onClick={() => handleDelete(rowData.id)} />
                                    </>
                                )}
                            />
                        </DataTable>
                    )}
                </div>
            </div>
        </Layout2>
    )
}

export default RestaurantList